// services/verificationService.js
// API calls for email and phone verification flow

const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';

/**
 * Helper to build the standard Odoo-Firebase API payload
 */
const buildPayload = (authToken, resId, resMethod, resParams = {}) => ({
  params: {
    access_token: process.env.REACT_APP_ACCESS_TOKEN,
    account_id: parseInt(process.env.REACT_APP_ACCOUNT_ID, 10) || 1,
    auth_token: authToken,
    res_id: resId,
    res_model: 'dais.user',
    res_method: resMethod,
    res_params: resParams
  }
});

/**
 * Helper to make API calls
 */
const apiCall = async (payload) => {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return response.json();
};

/**
 * Get a fresh Firebase auth token
 */
const getFreshToken = async () => {
  try {
    const { auth } = await import('./FirebaseService');
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken(true);
    }
    return localStorage.getItem('daisies_auth_token') || '';
  } catch {
    return localStorage.getItem('daisies_auth_token') || '';
  }
};

// =============================================
// EMAIL VERIFICATION
// =============================================

/**
 * Send verification email to the user.
 * Calls dais.user → send_verify_email
 */
export const sendVerificationEmail = async (userId) => {
  try {
    const authToken = await getFreshToken();
    const payload = buildPayload(authToken, parseInt(userId), 'send_verify_email');
    const data = await apiCall(payload);

    if (data.result === true || data.result?.success) {
      return { success: true };
    }

    return {
      success: false,
      error: data.result?.message || 'Error al enviar el correo de verificación'
    };
  } catch (error) {
    return { success: false, error: error.message || 'Error de conexión' };
  }
};

/**
 * Get email verification data (including verification link).
 * Calls dais.user → email_verification_data
 */
export const getEmailVerificationData = async (userId) => {
  try {
    const authToken = await getFreshToken();
    const payload = buildPayload(authToken, parseInt(userId), 'email_verification_data');
    const data = await apiCall(payload);

    if (data.result?.verification_link) {
      return {
        success: true,
        verificationLink: data.result.verification_link
      };
    }

    if (data.result?.data?.verification_link) {
      return {
        success: true,
        verificationLink: data.result.data.verification_link
      };
    }

    return {
      success: false,
      error: data.result?.message || 'No se pudo obtener el enlace de verificación'
    };
  } catch (error) {
    return { success: false, error: error.message || 'Error de conexión' };
  }
};

/**
 * Check the current verification state of the user.
 * Calls dais.user → validate_currently_user_state
 * Returns the user's step_state so we can detect when email is verified.
 */
export const checkUserVerificationState = async (userId) => {
  try {
    const authToken = await getFreshToken();
    const payload = buildPayload(authToken, parseInt(userId), 'validate_currently_user_state');
    const data = await apiCall(payload);

    // Handle multiple response shapes from the Odoo API:
    // shape A: { result: { success: true, data: { step_state: '...' } } }
    // shape B: { result: { step_state: '...' } }
    // shape C: { result: { success: true, step_state: '...' } }
    const stepState =
      data.result?.data?.step_state ||
      data.result?.step_state ||
      (data.result?.success !== false ? data.result?.state : undefined);

    if (stepState) {
      return { success: true, state: stepState };
    }

    // If result is an object but none of the known shapes matched, log for debugging
    if (data.result && typeof data.result === 'object') {
      console.warn('[checkUserVerificationState] Unexpected response shape:', JSON.stringify(data.result).slice(0, 300));
    }

    return {
      success: false,
      error: data.result?.message || data.result?.error || 'No se pudo verificar el estado'
    };
  } catch (error) {
    return { success: false, error: error.message || 'Error de conexión' };
  }
};

// =============================================
// EMAIL → NEXT STEP TRANSITION
// =============================================

/**
 * Tell Odoo to transition the user past the email-verification step.
 * Calls dais.user → change_step_pending_email_verification_to_next_step
 * This mirrors the FlutterFlow mobile app flow.
 */
export const changeStepToNextAfterEmailVerification = async (userId) => {
  try {
    const authToken = await getFreshToken();
    const payload = buildPayload(
      authToken,
      parseInt(userId),
      'change_step_pending_email_verification_to_next_step'
    );
    const data = await apiCall(payload);
    console.log('[changeStepToNextAfterEmailVerification] response:', data);

    // Aceptar varias formas de "éxito":
    // - result === true
    // - result.success === true
    // - result.data.step_state existe y NO es de email pendiente
    // - result tiene step_state directamente
    const stepState =
      data.result?.data?.step_state ||
      data.result?.step_state ||
      data.result?.state;

    const isPendingEmail = stepState && /pending[_-]?(verification[_-]?)?email/i.test(String(stepState));

    if (data.result === true || data.result?.success || (stepState && !isPendingEmail)) {
      return { success: true, data: data.result, stepState };
    }

    // Errores de Odoo pueden venir en data.error
    const odooErr = data.error?.data?.message || data.error?.message;
    return {
      success: false,
      error: data.result?.message || data.result?.error || odooErr || 'El correo aún no se ha verificado'
    };
  } catch (error) {
    return { success: false, error: error.message || 'Error de conexión' };
  }
};

// =============================================
// PHONE VERIFICATION
// =============================================

/**
 * Send phone verification code via WhatsApp/SMS.
 * Calls dais.user → send_verification_code_to_phone
 */
export const sendPhoneVerificationCode = async (userId) => {
  try {
    const authToken = await getFreshToken();
    const payload = buildPayload(authToken, parseInt(userId), 'send_verification_code_to_phone');
    const data = await apiCall(payload);

    if (data.result?.success) {
      return { success: true, data: data.result.data };
    }

    return {
      success: false,
      error: data.result?.message || 'Error al enviar el código de verificación'
    };
  } catch (error) {
    return { success: false, error: error.message || 'Error de conexión' };
  }
};

/**
 * Change the user's phone number.
 * Calls dais.user → change_user_mobile
 * Mirrors FlutterFlow "POST Change User Phone" action.
 *
 * @param {number|string} userId
 * @param {string} phone        – 10-digit phone number
 * @param {number} countryPhoneId – Odoo country phone ID (default 156 = Mexico)
 */
export const changeUserPhone = async (userId, phone, countryPhoneId = 156) => {
  try {
    const authToken = await getFreshToken();
    const payload = buildPayload(authToken, parseInt(userId), 'change_user_mobile', {
      mobile: phone,
      country_phone_id: countryPhoneId
    });
    const data = await apiCall(payload);

    if (data.result?.success || data.result === true) {
      return { success: true, data: data.result };
    }

    return {
      success: false,
      error: data.result?.message || 'No se pudo actualizar el teléfono'
    };
  } catch (error) {
    return { success: false, error: error.message || 'Error de conexión' };
  }
};

/**
 * Validate the phone verification code.
 * Calls dais.user → validate_verification_code_to_phone
 */
export const validatePhoneCode = async (userId, code, notificationId) => {
  try {
    const authToken = await getFreshToken();
    const payload = buildPayload(authToken, parseInt(userId), 'validate_verification_code_to_phone', {
      code: code,
      notification_id: notificationId
    });
    const data = await apiCall(payload);

    if (data.result?.success) {
      return { success: true, data: data.result.data };
    }

    return {
      success: false,
      error: data.result?.message || 'Código de verificación incorrecto'
    };
  } catch (error) {
    return { success: false, error: error.message || 'Error de conexión' };
  }
};
